import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";

import HeaderCard from "./workspace/HeaderCard.jsx";
import CallModePanel from "./workspace/CallModePanel.jsx";
import QuickActions from "./workspace/QuickActions.jsx";
import TabBar from "./workspace/TabBar.jsx";
import NewConstructionPanel from "./workspace/NewConstructionPanel.jsx";
import ConvertDealTypeButton from "./workspace/ConvertDealTypeButton.jsx";
import ShareDealButton from "@/components/v3/ShareDealButton.jsx";
import { resolveTabComponent } from "./workspace/tabRegistry.js";
import { useActiveProperty } from "@/context/ActivePropertyContext";

/* Property Workspace — the manifest-driven shell.

   Loads:
     1) GET /v3/properties/{id}                     — property doc
     2) GET /v3/properties/{id}/workspace-manifest  — ordered tab list

   Renders each tab by looking up the frontend component registered under
   that tab id. Tabs the backend marks `enabled=false` render ComingSoonTab.
*/
// Tabs that don't apply when the property's `deal_type === "land"`.
// Kept on the frontend (rather than the backend registry) so we can
// tune the list without a redeploy while land support is still evolving.
const HOUSE_ONLY_TABS = new Set([
  "repair_estimate", "fixflip", "rental", "brrrr", "wholetail",
]);

export default function PropertyWorkspace() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [property, setProperty] = useState(null);
  const [tabs, setTabs] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [callOpen, setCallOpen] = useState(false);
  const { setPropertyId } = useActiveProperty();

  // Keep the top-bar Active Property pill in sync with whatever property
  // the operator opened via /v3/properties/{id}. This is the whole point
  // of the ReSimpli-style context unification: navigate anywhere and the
  // rest of the app follows you.
  useEffect(() => {
    if (id) setPropertyId(id);
  }, [id, setPropertyId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [propRes, manifestRes] = await Promise.all([
        api.get(`/v3/properties/${id}`),
        api.get(`/v3/properties/${id}/workspace-manifest`),
      ]);
      setProperty(propRes.data);
      // Land support (L1): hide house-only tabs when this property is
      // flagged as land. Zero-cost — filter happens purely in the client.
      const rawList = manifestRes.data.tabs || [];
      const list = (propRes.data?.deal_type === "land")
        ? rawList.filter((t) => !HOUSE_ONLY_TABS.has(t.id))
        : rawList;
      setTabs(list);
      // Deep-link support: ?tab=deal-score focuses a specific tab if
      // it exists and is enabled. Otherwise fall back to the first enabled.
      const requested = searchParams.get("tab");
      const requestedTab = requested
        ? list.find((t) => t.id === requested && t.enabled)
        : null;
      const firstEnabled = list.find((t) => t.enabled) || list[0];
      setActiveId((requestedTab || firstEnabled)?.id || null);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load property");
    } finally {
      setLoading(false);
    }
  }, [id, searchParams]);

  useEffect(() => { load(); }, [load]);

  const activeTab = useMemo(
    () => tabs.find((t) => t.id === activeId) || null,
    [tabs, activeId],
  );
  const ActiveTabComponent = useMemo(
    () => (activeTab ? resolveTabComponent(activeTab) : null),
    [activeTab],
  );

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-600 text-sm" data-testid="workspace-loading">
        <Loader2 size={14} className="animate-spin" /> Loading workspace…
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-md border border-red-300 bg-red-500/5 p-4 text-sm text-red-800"
           data-testid="workspace-error">
        <div>{error}</div>
        <Link to="/v3/properties" className="mt-3 inline-flex items-center gap-1 text-xs text-slate-700 hover:text-slate-900">
          <ArrowLeft size={12} /> Back to properties
        </Link>
      </div>
    );
  }
  if (!property) return null;

  return (
    <div className="space-y-4" data-testid="property-workspace">
      <div className="flex items-center gap-2 text-xs text-slate-600">
        <Link to="/v3/properties" className="inline-flex items-center gap-1 hover:text-slate-900"
              data-testid="workspace-back">
          <ArrowLeft size={12} /> Properties
        </Link>
      </div>

      <HeaderCard property={property} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <QuickActions
          property={property}
          tabs={tabs}
          onSelectTab={(nextId) => {
            setActiveId(nextId);
            setSearchParams(
              (p) => { p.set("tab", nextId); return p; },
              { replace: true },
            );
          }}
        />
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setCallOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-lg font-black text-[13px] px-3.5 py-2 text-white shadow-md shadow-indigo-900/25 hover:shadow-lg transition-all"
            style={{ backgroundImage: "linear-gradient(135deg, #6366F1, #4F46E5)" }}
            data-testid="deal-start-call"
            title="Open Call Mode — the guided seller-call co-pilot"
          >
            <span className="h-4 w-4 rounded-full bg-white/20 grid place-items-center text-[10px]">●</span>
            Start Call
          </button>
          <ConvertDealTypeButton
            property={property}
            onConverted={(updated) => {
              setProperty(updated);
              // Deal type flip changes which tabs apply (land vs house
              // tabs, new-construction panel, etc.) — refetch the
              // manifest so the tab bar rebuilds. This is what the
              // production report was failing on.
              load();
            }}
          />
          <ShareDealButton propertyId={property.id} />
        </div>
      </div>

      {/* Call Mode overlay — right-side slide-in co-pilot */}
      <CallModePanel
        property={property}
        open={callOpen}
        onClose={() => setCallOpen(false)}
        onMetricsUpdate={(m) => setProperty((p) => p ? { ...p, metrics: m } : p)}
      />

      {/* New Construction module — 3-card affordable-housing panel shown
          inline above the standard tab bar when the deal is a build. */}
      {property.deal_type === "new_construction" && (
        <NewConstructionPanel property={property} />
      )}

      <div className="mt-6">
        <TabBar tabs={tabs} activeId={activeId} onSelect={(nextId) => {
          setActiveId(nextId);
          // Keep the URL in sync so the tab is deep-linkable and shareable.
          const params = new URLSearchParams(searchParams);
          if (nextId) params.set("tab", nextId); else params.delete("tab");
          setSearchParams(params, { replace: true });
        }} />
        <div className="pt-5">
          {ActiveTabComponent && activeTab ? (
            <ActiveTabComponent tab={activeTab} property={property} onNavigateTab={setActiveId} />
          ) : (
            <div className="text-sm text-slate-600">No tabs available.</div>
          )}
        </div>
      </div>
    </div>
  );
}
